/**
 * Live numeric design studies. The host supplies one interpolated speed and
 * timestamped history; these faces never invent telemetry, targets or alarms.
 */
export const numericDesignNames = Object.freeze({
  odometer: "Rolling instrument",
  horizon: "Flowing horizon",
  ribbon: "History ribbon",
});

const states = new WeakMap();
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const fixed = (value, digits = 1) => Number(value).toFixed(digits);
const coral = "#d9775f";
const quiet = "#77777d";
const muted = "#a4a4ab";
const white = "#f5f5f7";

function text(x, y, value, size = 11, color = muted, extra = "") {
  return `<text x="${x}" y="${y}" font-size="${size}" fill="${color}" ${extra}>${value}</text>`;
}

function start(variant, uid) {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 260" data-numeric-design="${variant}" role="img" aria-label="${numericDesignNames[variant]}: speed over ground" style="display:block;width:100%;height:auto;overflow:visible;font-family:TCInter,Inter,-apple-system,BlinkMacSystemFont,sans-serif;font-variant-numeric:tabular-nums">
    <defs>
      <linearGradient id="${uid}-glass" x1="0" y1="0" x2="0" y2="1"><stop stop-color="#353538" stop-opacity=".5"/><stop offset=".45" stop-color="#101012"/><stop offset="1" stop-color="#242427"/></linearGradient>
      <linearGradient id="${uid}-fade" x1="0" y1="0" x2="0" y2="1"><stop stop-color="#151517"/><stop offset=".3" stop-color="#151517" stop-opacity="0"/><stop offset=".7" stop-color="#151517" stop-opacity="0"/><stop offset="1" stop-color="#151517"/></linearGradient>
      <linearGradient id="${uid}-area" x1="0" y1="0" x2="0" y2="1"><stop stop-color="${coral}" stop-opacity=".28"/><stop offset="1" stop-color="${coral}" stop-opacity="0"/></linearGradient>
      <linearGradient id="${uid}-bar" x1="0" y1="0" x2="0" y2="1"><stop stop-color="#b4b4bc" stop-opacity=".9"/><stop offset="1" stop-color="#62626b" stop-opacity=".18"/></linearGradient>
      <linearGradient id="${uid}-coral-bar" x1="0" y1="0" x2="0" y2="1"><stop stop-color="#f0a08a"/><stop offset="1" stop-color="${coral}" stop-opacity=".16"/></linearGradient>
      <clipPath id="${uid}-reels"><rect x="24" y="51" width="287" height="113" rx="18"/></clipPath>
    </defs>`;
}

function reel(place, x) {
  return `<g data-reel="${place}" transform="translate(${x} 0)">
    <g data-reel-digits="${place}">
      ${[-1, 0, 1].map((position) => text(0, 131 + position * 92, "0", 80, white, `text-anchor="middle" font-weight="600" letter-spacing="-5" data-digit="${position}"`)).join("")}
    </g>
  </g>`;
}

function odometer(uid) {
  return `<rect x="19" y="43" width="322" height="130" rx="23" fill="url(#${uid}-glass)" stroke="#424247" stroke-opacity=".55"/>
    <path d="M43 44H317" stroke="#ffffff" stroke-opacity=".08"/>
    <path d="M42 172H318" stroke="#000000" stroke-opacity=".6"/>
    <g clip-path="url(#${uid}-reels)">${reel(100, 53)}${reel(10, 109)}${reel(1, 170)}${reel(0.1, 266)}</g>
    <rect x="24" y="51" width="287" height="113" rx="18" fill="url(#${uid}-fade)" pointer-events="none"/>
    <path d="M211 65V150" stroke="#535359" stroke-opacity=".28"/>
    <circle cx="217" cy="132" r="4.2" fill="${coral}"/>
    ${text(319, 139, "kn", 16, muted, 'text-anchor="middle" font-weight="550"')}
    ${text(25, 25, "SPEED OVER GROUND", 10, quiet, 'letter-spacing="2"')}
    ${text(334, 25, "0.1 kn", 10, quiet, 'text-anchor="end"')}
    <path d="M25 203H334" stroke="#4b4b50" stroke-width="3" stroke-linecap="round"/>
    <path data-part="range-fill" d="M25 203H25" stroke="${coral}" stroke-opacity=".45" stroke-width="3" stroke-linecap="round"/>
    <circle data-part="range-dot" cx="25" cy="203" r="5" fill="${coral}" stroke="#1c1c1e" stroke-width="3"/>
    <path d="M25 197V209M334 197V209" stroke="#66666c" stroke-width="1"/>
    ${text(25, 229, "—", 12, white, 'data-part="low"')}
    ${text(334, 229, "—", 12, white, 'data-part="high" text-anchor="end"')}
    ${text(180, 229, "RECENT RANGE · kn", 9, quiet, 'letter-spacing="1.1" text-anchor="middle"')}
    ${text(180, 251, "", 9, quiet, 'text-anchor="middle" data-part="duration"')}`;
}

function horizon(uid) {
  return `${text(24, 26, "SPEED OVER GROUND", 10, quiet, 'letter-spacing="2"')}
    ${text(19, 103, "—", 88, white, 'font-weight="550" letter-spacing="-5" data-part="speed"')}
    ${text(181, 98, "kn", 22, muted, 'font-weight="450" data-part="unit"')}
    ${text(335, 63, "—", 13, coral, 'font-weight="600" text-anchor="end" data-part="delta"')}
    ${text(335, 81, "since first sample", 9, quiet, 'text-anchor="end"')}
    <path d="M25 136H335M25 174H335M25 212H335" stroke="#414147" stroke-opacity=".35" stroke-dasharray="2 6"/>
    <path data-part="history-area" d="M25 212H335" fill="url(#${uid}-area)"/>
    <path data-part="history-glow" d="M25 212H335" fill="none" stroke="${coral}" stroke-width="9" stroke-opacity=".08" stroke-linecap="round"/>
    <path data-part="history-line" d="M25 212H335" fill="none" stroke="${coral}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
    <path data-part="history-guide" d="M335 136V224" stroke="${coral}" stroke-opacity=".12"/>
    <circle data-part="history-halo" cx="335" cy="212" r="9" fill="${coral}" fill-opacity=".1"/>
    <circle data-part="history-dot" cx="335" cy="212" r="4.5" fill="${coral}" stroke="#1c1c1e" stroke-width="2"/>
    ${text(25, 243, "—", 10, muted, 'data-part="duration"')}
    ${text(335, 243, "now", 10, muted, 'text-anchor="end"')}
    ${text(335, 126, "—", 9, quiet, 'data-part="high" text-anchor="end"')}`;
}

function ribbon(uid) {
  return `${text(24, 26, "SPEED OVER GROUND", 10, quiet, 'letter-spacing="2"')}
    ${text(20, 98, "—", 82, white, 'font-weight="550" letter-spacing="-5" data-part="speed"')}
    ${text(177, 93, "kn", 20, muted, 'data-part="unit"')}
    <rect x="252" y="46" width="83" height="32" rx="16" fill="#29282b"/>
    ${text(294, 67, "—", 13, coral, 'text-anchor="middle" font-weight="650" data-part="delta"')}
    ${text(335, 93, "since first sample", 9, quiet, 'text-anchor="end"')}
    ${text(25, 126, "RECENT SPEED", 9, quiet, 'letter-spacing="1.5"')}
    ${text(335, 126, "—", 9, quiet, 'text-anchor="end" data-part="duration"')}
    <path d="M25 211H335" stroke="#535359" stroke-opacity=".45"/>
    ${Array.from({ length: 15 }, (_, i) => `<rect data-bar="${i}" x="${25 + i * 21}" y="207" width="14" height="4" rx="3" fill="url(#${uid}-${i === 14 ? "coral-" : ""}bar)" opacity="${i === 14 ? 1 : 0.3 + i / 20}"/>`).join("")}
    <path d="M25 229H335" stroke="#45454b" stroke-width="2" stroke-linecap="round"/>
    <path data-part="range-fill" d="M25 229H25" stroke="${coral}" stroke-opacity=".45" stroke-width="2" stroke-linecap="round"/>
    <circle data-part="range-dot" cx="25" cy="229" r="4" fill="${coral}"/>
    ${text(25, 252, "—", 11, white, 'data-part="low"')}
    ${text(335, 252, "—", 11, white, 'text-anchor="end" data-part="high"')}
    ${text(180, 252, "RECENT RANGE · kn", 9, quiet, 'text-anchor="middle" letter-spacing="1"')}`;
}

/** Return a complete responsive SVG. uid only namespaces SVG paint resources. */
export function createNumericDesign(variant, uid = variant) {
  if (!Object.hasOwn(numericDesignNames, variant))
    throw new Error(`Unknown numeric design: ${variant}`);
  uid = `tc-number-${String(uid).replace(/[^a-zA-Z0-9_-]/g, "-")}`;
  const faces = { odometer, horizon, ribbon };
  return `${start(variant, uid)}${faces[variant](uid)}</svg>`;
}

function element(root, name) {
  return root.querySelector(`[data-part="${name}"]`);
}
function setText(root, name, value) {
  const node = element(root, name);
  if (node) node.textContent = value;
}
function setAttr(root, name, attribute, value) {
  const node = element(root, name);
  if (node) node.setAttribute(attribute, String(value));
}

function eased(state, key, goal, alpha) {
  const previous = state[key];
  const value = Number.isFinite(previous)
    ? previous + (goal - previous) * alpha
    : goal;
  state[key] = value;
  return value;
}

function sample(history, count) {
  if (history.length === 1)
    return Array.from({ length: count }, () => history[0].speed);
  return Array.from({ length: count }, (_, i) => {
    const position = (i / (count - 1)) * (history.length - 1);
    const lower = Math.floor(position);
    const upper = Math.min(history.length - 1, lower + 1);
    return (
      history[lower].speed +
      (history[upper].speed - history[lower].speed) * (position - lower)
    );
  });
}

function curve(points) {
  // Cubic segments keep the history fluid without inventing extra extrema.
  return points.reduce((path, point, index) => {
    if (!index) return `M${fixed(point.x, 2)} ${fixed(point.y, 2)}`;
    const previous = points[index - 1];
    const middle = (point.x + previous.x) / 2;
    return `${path} C${fixed(middle, 2)} ${fixed(previous.y, 2)} ${fixed(middle, 2)} ${fixed(point.y, 2)} ${fixed(point.x, 2)} ${fixed(point.y, 2)}`;
  }, "");
}

function durationLabel(history) {
  const seconds = Math.max(
    0,
    (history.at(-1).measuredAt - history[0].measuredAt) / 1000,
  );
  if (seconds < 1) return "one sample";
  if (seconds < 60) return `last ${Math.round(seconds)} s`;
  return `last ${Math.round(seconds / 60)} min`;
}

/**
 * Call every animation frame with the host's interpolated reading. Root can be
 * the returned SVG or a containing element. No CSS animation or random data.
 */
export function updateNumericDesign(root, variant, reading, history = []) {
  if (!Object.hasOwn(numericDesignNames, variant))
    throw new Error(`Unknown numeric design: ${variant}`);
  if (root?.getAttribute?.("data-numeric-design") !== variant)
    root = root?.querySelector?.(`[data-numeric-design="${variant}"]`);
  if (!root) throw new Error("Numeric design SVG was not found");
  const speed = reading?.speed;
  if (!Number.isFinite(speed) || speed < 0) {
    root.setAttribute("aria-label", "Speed over ground unavailable");
    root.setAttribute("opacity", ".4");
    return;
  }
  root.removeAttribute("opacity");
  root.setAttribute(
    "aria-label",
    `${numericDesignNames[variant]}: ${fixed(speed)} knots speed over ground`,
  );
  const now = globalThis.performance?.now?.() ?? Date.now();
  let state = states.get(root);
  if (!state) {
    state = { lastTime: now };
    states.set(root, state);
  }
  const alpha = 1 - Math.exp(-clamp(now - state.lastTime, 1, 100) / 150);
  state.lastTime = now;
  let samples = (Array.isArray(history) ? history : [])
    .filter(
      (entry) =>
        Number.isFinite(entry?.speed) &&
        entry.speed >= 0 &&
        Number.isFinite(entry?.measuredAt),
    )
    .map((entry) => ({ ...entry }))
    .sort((a, b) => a.measuredAt - b.measuredAt)
    .slice(-60);
  const measuredAt = Number.isFinite(reading?.measuredAt)
    ? reading.measuredAt
    : (samples.at(-1)?.measuredAt ?? 0);
  // Replace an equal/newer endpoint with the currently interpolated reading.
  // This makes the curve and its endpoint move together between host updates.
  samples = samples.filter((entry) => entry.measuredAt < measuredAt);
  samples.push({ speed, measuredAt });
  const values = samples.map((entry) => entry.speed);
  const low = eased(state, "low", Math.min(...values), alpha);
  const high = eased(state, "high", Math.max(...values), alpha);
  const delta = eased(state, "delta", speed - samples[0].speed, alpha);
  const span = Math.max(0.2, high - low);
  const position = 25 + clamp((speed - low) / span, 0, 1) * 309;
  setText(root, "speed", fixed(speed));
  setText(root, "low", fixed(low));
  setText(root, "high", `${fixed(high)}${variant === "horizon" ? " kn" : ""}`);
  setText(
    root,
    "delta",
    `${delta >= 0 ? "+" : "−"}${fixed(Math.abs(delta))} kn`,
  );
  setText(root, "duration", durationLabel(samples));
  const digits = fixed(speed).length;
  const fontSize =
    variant === "horizon" ? (digits > 4 ? 68 : 88) : digits > 4 ? 64 : 82;
  setAttr(root, "speed", "font-size", fontSize);
  setAttr(root, "unit", "x", 30 + fontSize * ((digits - 1) * 0.61 + 0.26));
  const railY = variant === "odometer" ? 203 : 229;
  setAttr(root, "range-fill", "d", `M25 ${railY}H${fixed(position, 2)}`);
  setAttr(root, "range-dot", "cx", fixed(position, 2));

  if (variant === "odometer") {
    // Each reel physically rolls through neighboring digits. Whole digits only
    // roll at the carry boundary; the tenths reel rolls continuously.
    const bounded = clamp(speed, 0, 999.9);
    for (const place of [100, 10, 1, 0.1]) {
      const divided = bounded / place + 1e-8;
      const digit = Math.floor(divided) % 10;
      const remainder = bounded % place;
      const progress =
        place === 0.1
          ? divided - Math.floor(divided)
          : clamp((remainder - (place - 0.1)) / 0.1, 0, 1);
      const reelRoot = root.querySelector(`[data-reel="${place}"]`);
      reelRoot.setAttribute(
        "opacity",
        place === 100 && bounded < 99.9
          ? "0"
          : place === 10 && bounded < 9.9
            ? ".3"
            : "1",
      );
      reelRoot
        .querySelector(`[data-reel-digits="${place}"]`)
        .setAttribute("transform", `translate(0 ${fixed(-progress * 92, 2)})`);
      for (const child of reelRoot.querySelectorAll("[data-digit]")) {
        const offset = Number(child.getAttribute("data-digit"));
        child.textContent = String((digit + offset + 10) % 10);
      }
    }
    return;
  }

  if (variant === "horizon") {
    const points = sample(samples, 28).map((value, index) => {
      const plotY = 211 - clamp((value - low) / span, 0, 1) * 69;
      return {
        x: 25 + (index / 27) * 309,
        y: eased(state, `point-${index}`, plotY, alpha),
      };
    });
    const line = curve(points);
    setAttr(root, "history-line", "d", line);
    setAttr(root, "history-glow", "d", curve(points.slice(-5)));
    setAttr(root, "history-area", "d", `${line} L334 224L25 224Z`);
    setAttr(root, "history-dot", "cy", fixed(points.at(-1).y, 2));
    setAttr(root, "history-halo", "cy", fixed(points.at(-1).y, 2));
    return;
  }

  const scaleHigh = Math.max(1, high * 1.1);
  const bars = samples.slice(-15).map((entry) => entry.speed);
  const padding = 15 - bars.length;
  for (let index = 0; index < 15; index++) {
    const bar = root.querySelector(`[data-bar="${index}"]`);
    const isPopulated = index >= padding;
    bar.setAttribute("visibility", isPopulated ? "visible" : "hidden");
    if (!isPopulated) continue;
    const height = eased(
      state,
      `bar-${index}`,
      Math.max(3, clamp(bars[index - padding] / scaleHigh, 0, 1) * 70),
      alpha,
    );
    bar.setAttribute("height", fixed(height, 2));
    bar.setAttribute("y", fixed(211 - height, 2));
  }
}
